"use client"

import type React from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState, useRef, useMemo } from "react"
import { AsciiBanner } from "./ascii-banner"
import { ThemeProvider } from "./theme-provider"
import { Footer } from "./footer"
import { ReleaseNotesDialog } from "./release-notes-dialog"
import { getLatestReleaseNote } from "../lib/data/release-notes"
import { Toaster, toast } from "sonner"
import { SidebarProvider } from "../ui/sidebar"
import { DevToolsDetector } from "./devtools-detector"
import { setAuthToken, setAuthCookie, fetchCurrentUserFromApi, getAuthToken, getAccountApiUrl } from "../lib/auth"
import { addAccount } from "../lib/multi-account"
import { ChatDrawer } from "./chat-drawer"
import { AnalyticsRecorder } from "./analytics-recorder"
import {
  parseMentions,
  resolveMentions,
  formatContextBlock,
  registerDefaultProviders,
  recommendSurface,
  looksLikeCodingGoal,
  type ContextAdapters,
} from "@askfinz/shared-code"

/** P2 UI hint for the webIDE GlobalChatDrawer — nudges to a better surface when a
 *  coding goal fits one. Active only on the code-IDE `/project/{id}` route. */
function webIdeSurfaceHint(message: string): string | null {
  if (typeof window === "undefined") return null
  if (!/\/project\//.test(window.location.pathname)) return null
  if (!looksLikeCodingGoal(message)) return null
  const rec = recommendSurface(message)
  if (!rec.matched || rec.surface.key === "webide") return null
  return `Better on ${rec.surface.name}`
}

/** P3 receive-side inbox for the webIDE: poll/accept/decline handovers addressed to
 *  'webide'. Active only on the code-IDE `/project/{id}` route. */
const webIdeHandoverInbox = {
  poll: async (): Promise<Array<{ id: string; from_surface: string; message: string; reason: string }>> => {
    if (typeof window === "undefined") return []
    const m = window.location.pathname.match(/\/project\/([^/]+)/)
    const projectId = m?.[1]
    if (!projectId) return []
    try {
      const token = getAuthToken()
      const res = await fetch(`/api/orchestrator/handover/pending?surface=webide&conversation_key=${encodeURIComponent(projectId + ":main")}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return []
      const data = await res.json()
      return (data.handovers ?? []).map((h: { id: string; from_surface: string; message: string; reason: string }) => ({
        id: h.id, from_surface: h.from_surface, message: h.message, reason: h.reason,
      }))
    } catch { return [] }
  },
  accept: async (id: string): Promise<void> => {
    const token = getAuthToken()
    await fetch(`/api/orchestrator/handover/${id}/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ surface: "webide" }),
    })
  },
  decline: async (id: string): Promise<void> => {
    const token = getAuthToken()
    await fetch(`/api/orchestrator/handover/${id}/decline`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ surface: "webide" }),
    })
  },
  // P4 SSE push — same-origin, so EventSource sends the HttpOnly auth cookie, which is
  // what the (authenticated, per-user since SECURITY V-105) stream checks.
  streamUrl: (): string | null => {
    if (typeof window === "undefined") return null
    const m = window.location.pathname.match(/\/project\/([^/]+)/)
    const projectId = m?.[1]
    if (!projectId) return null
    return `/api/orchestrator/handover/stream?surface=webide&conversation_key=${encodeURIComponent(projectId + ":main")}`
  },
}

/** webIDE @-context providers surfaced in the drawer's `@` menu for discoverability.
 *  Resolution is handled by `resolveWebIdeContextBlock`; this list just makes them
 *  visible/insertable. Mirrors the adapters wired there (file/git/codebase). */
const WEBIDE_CONTEXT_PROVIDERS = [
  { name: "file", description: "Reference a file's contents", takesArg: true },
  { name: "git", description: "Working-tree status & current branch", takesArg: false },
  { name: "codebase", description: "Semantic search across the repo", takesArg: true },
]

/** P3: clicking the chip hands the task to the recommended surface via the
 *  orchestrator (`/api/orchestrator/handover`). Context follows the shared
 *  conversation key; this signals the target surface to pick up. */
async function webIdeHandover(message: string): Promise<void> {
  if (typeof window === "undefined") return
  const m = window.location.pathname.match(/\/project\/([^/]+)/)
  const projectId = m?.[1]
  if (!projectId) return
  const rec = recommendSurface(message)
  if (!rec.matched || rec.surface.key === "webide") return
  try {
    const token = getAuthToken()
    const res = await fetch("/api/orchestrator/handover", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        conversation_key: `${projectId}:main`,
        from_surface: "webide",
        to_surface: rec.surface.key,
        message,
        reason: rec.reason,
      }),
    })
    if (res.ok) toast.success(`Handed to ${rec.surface.name}`, { description: "Open it to continue — your conversation follows you." })
    else toast.error("Handover failed", { description: `HTTP ${res.status}` })
  } catch (e) {
    toast.error("Handover failed", { description: e instanceof Error ? e.message : "network error" })
  }
}

// Register the shared @-context providers once (idempotent) for the GlobalChatDrawer.
registerDefaultProviders()

/**
 * webIDE @-context resolver for the GlobalChatDrawer. Active ONLY on the code IDE's
 * `/project/{id}` route — extracts the projectId and resolves @file / @codebase / @git
 * client-side via the code backend routes (parity with the desktop drawer + CLI). On
 * every other app/route there's no projectId, so it returns "" and the drawer is
 * unchanged. Mirrors the agentic agent-panel wiring but on the GlobalChatDrawer surface
 * the directive names as primary.
 */
async function resolveWebIdeContextBlock(message: string): Promise<string> {
  if (typeof window === "undefined") return ""
  const m = window.location.pathname.match(/\/project\/([^/]+)/)
  const projectId = m?.[1]
  if (!projectId) return ""
  const mentions = parseMentions(message)
  if (!mentions.length) return ""
  const auth = (): Record<string, string> => {
    const t = getAuthToken()
    return t ? { Authorization: `Bearer ${t}` } : {}
  }
  const adapters: ContextAdapters = {
    readFile: async (p: string) => {
      const res = await fetch(`/api/v1/files/${projectId}/read?path=${encodeURIComponent(p)}`, { headers: auth() })
      if (!res.ok) throw new Error(`read ${p} failed`)
      return (await res.json()).content ?? ""
    },
    gitStatus: async () => {
      const [s, b] = await Promise.all([
        fetch(`/api/v1/git/status/${projectId}`, { headers: auth() }),
        fetch(`/api/v1/git/branch/${projectId}`, { headers: auth() }),
      ])
      const staged: string[] = [], modified: string[] = [], untracked: string[] = []
      let branch = ""
      if (b.ok) branch = ((await b.json()).branch ?? "")
      if (s.ok) {
        for (const row of (await s.json()) as Array<{ path: string; status: string }>) {
          const st = String(row.status).toLowerCase()
          if (st.includes("untrack")) untracked.push(row.path)
          else if (st.includes("stag") || st.includes("add")) staged.push(row.path)
          else modified.push(row.path)
        }
      }
      return { branch, staged, modified, untracked }
    },
    search: async (query: string) => {
      try {
        const res = await fetch(`/api/v1/knowledge/${projectId}/search?q=${encodeURIComponent(query)}&limit=12`, { headers: auth() })
        if (!res.ok) return []
        const data = await res.json()
        return (data.results ?? []).map((r: { file_path?: string; snippet?: string; chunk_text?: string }) => ({
          path: r.file_path ?? "",
          snippet: r.snippet ?? r.chunk_text ?? "",
        }))
      } catch { return [] }
    },
  }
  try {
    const items = await resolveMentions(mentions, { surface: "webide", adapters })
    return formatContextBlock(items)
  } catch { return "" }
}

/**
 * Combined @-context resolver for the GlobalChatDrawer. Runs the webIDE resolver
 * (active only on `/project/` routes) AND any host-app resolver published at
 * `window.__askfinzResolveContextBlock` (e.g. stream's @video/@channel/@analytics),
 * concatenating both blocks. Host apps self-wire via that window global, so no
 * per-app prop is needed here. Returns "" when neither contributes (unchanged).
 */
async function resolveCombinedContextBlock(message: string): Promise<string> {
  let out = ""
  try { out += await resolveWebIdeContextBlock(message) } catch { /* */ }
  try {
    const w = window as unknown as { __askfinzResolveContextBlock?: (m: string) => Promise<string> }
    if (typeof w.__askfinzResolveContextBlock === "function") {
      const host = await w.__askfinzResolveContextBlock(message)
      if (host) out += host
    }
  } catch { /* */ }
  return out
}

// Note: AuthGuard should be provided by the consuming app or passed as a prop.
// import { AuthGuard } from "@/components/auth/auth-guard"

const DefaultAuthGuard = ({ children }: { children: React.ReactNode }) => <>{children}</>;


import Script from "next/script"
import { Suspense } from "react"

function AuthTokenHandler() {
  const searchParams = useSearchParams()

  // Persist token from URL (post-login redirect from another app) and remove from URL.
  //
  // Two query params are recognised:
  //   - ?token=     replace flow. The receiving app's "current" identity
  //                 becomes <token>. Calls setAuthToken which also mirrors
  //                 the new identity into the multi-account list.
  //   - ?add_token= ADDITIVE flow (V-010 fix). The receiving app already has
  //                 an active identity; we want to APPEND <token> to the
  //                 multi-account list and make it the new active one
  //                 without dropping the previously-added identities.
  //
  // BUG (pre-fix): ?add_token used to fall through to setAuthToken() (the
  // ?token= path), which writes localStorage[AUTH_TOKEN_KEY] *first* and
  // *then* asynchronously calls addAccount. By the time addAccount reads
  // the existing list via getAccounts(), the legacy-token self-heal step
  // had already swapped the existing account's slot for the new token —
  // collapsing the list to one entry. The fix is to route ?add_token=
  // through addAccount() directly, which preserves the existing accounts
  // cookie before any legacy writes happen.
  useEffect(() => {
    if (typeof window === "undefined") return
    const replaceToken = searchParams.get("token")
    const addToken = searchParams.get("add_token")
    if (!replaceToken && !addToken) return

    const stripParams = () => {
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete("token")
        url.searchParams.delete("add_token")
        const clean = url.pathname + (url.search || "") + (url.hash || "")
        window.history.replaceState(window.history.state, "", clean)
      } catch { /* ignore */ }
    }

    try {
      if (addToken) {
        // ── Additive (multi-account) flow ─────────────────────────────
        // Step 1: register the new identity in the device-account list
        // BEFORE any legacy/localStorage/cookie writes. This call reads
        // the existing AskFinz_accounts_v1 cookie, finds the prior
        // identity (e.g. account A), pushes the new entry, then writes
        // [A, B] back. setActiveAccount() inside addAccount() handles
        // the legacy mirrors.
        //
        // Identity-id derivation uses the JWT `email`/`user_id`/`sub`
        // claim when user metadata is empty (see _identityId in
        // multi-account.ts). For empty-user calls that resolves
        // correctly for any well-formed AskFinz JWT.
        addAccount(addToken, {})
        // Step 2: enrich the entry with proper name/email/avatar by
        // calling /api/auth/me with the new token. Done async — the
        // entry already exists in the list, this just upgrades the
        // display metadata.
        const enrich = async () => {
          try {
            const res = await fetch(`${getAccountApiUrl()}/api/auth/me`, {
              headers: { Authorization: `Bearer ${addToken}` },
              credentials: "include",
            })
            if (!res.ok) return
            const u = await res.json()
            // Re-add with full user metadata. addAccount dedupes by
            // identity-id so this UPDATES the existing entry rather
            // than creating a duplicate.
            addAccount(addToken, {
              name: u?.name ?? u?.email ?? "User",
              email: u?.email ?? "",
              avatar: u?.avatar ?? "",
            })
          } catch { /* network blip — leave entry with empty user */ }
        }
        void enrich()
      } else if (replaceToken) {
        // ── Replace (single-account) flow ─────────────────────────────
        setAuthToken(replaceToken)
        setAuthCookie(replaceToken)
        // Also fetch and save user data if possible
        fetchCurrentUserFromApi().then(user => {
          if (user) {
            setAuthToken(replaceToken, user)
          }
        })
      }
      stripParams()
    } catch (_) { /* ignore */ }
  }, [searchParams])

  return null
}

const CONSOLE_ASCII_BANNER = `
                              AAAAAAAA
                          AAAAAAAAAAA
                       AAAAAAAAAAAAAA                                          AAAA              AAAAAAAAAAAAAAAAAA  AAAA
                    AAAAAAAAAAAAAAAAA                                          AAAA              AAAAAAAAAAAAAAAAAA  AAAA
                  AAAAAAAAAAAAAAAAAAA                                          AAAA              AAAA
                AAAAAAAAAAAAAAAAAAAA           AAAAAAAAA        AAAAAAAAAA     AAAA      AAAAA   AAAA                AAAA   AAAA  AAAAAAA     AAAAAAAAAAAAAAAA
              AAAAAAAAAAAAAAAAAAAAAA        AAAAAAAAAAAAAAA   AAAAAAAAAAAAAA   AAAA    AAAAAAA   AAAA                AAAA   AAAAAAAAAAAAAAA   AAAAAAAAAAAAAAAA
            AAAAAAAAAAAAAAAAAAAAAAAA       AAAAAA    AAAAAA   AAAAA    AAAAAA  AAAA   AAAAAA     AAAAAAAAAAAAAAAA    AAAA   AAAAAAAA  AAAAAA           AAAAAA
           AAAAAAAAAAAAAAAAAAAAAAAAA       AAAA        AAAA  AAAAAA      AAAA  AAAAAAAAAAA       AAAAAAAAAAAAAAAA    AAAA   AAAA       AAAAA         AAAAAAA
         AAAAAAAAAAAAAAAAAAAAAAAAAAAA         AAAAAAAAAAAAA   AAAAAAAAAAA      AAAAAAAAAA        AAAAAAAAAAAAAAAA    AAAA   AAAA        AAAA        AAAAAA
        AAAAAAAAAAAAAAAAAAAAAAAAAAAAA       AAAAAAAAAAAAAAA    AAAAAAAAAAAAAA  AAAAAAAAAAA       AAAA                AAAA   AAAA        AAAA      AAAAAAA
       AAAAAAAAAAAAAAAAAAAAAAAAAAAAAA      AAAAA       AAAA        AAAAAAAAAA  AAAAA  AAAAA      AAAA                AAAA   AAAA        AAAA    AAAAAAA
      AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA    AAAAA        AAAA  AAAA        AAAA  AAAA    AAAAA     AAAA                AAAA   AAAA        AAAA   AAAAAA
     AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA    AAAAAA    AAAAAAA  AAAAAA    AAAAAA  AAAA     AAAAAA   AAAA                AAAA   AAAA        AAAA AAAAAAAAAAAAAAAA
    AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA    AAAAAAAAAAAAAAAA   AAAAAAAAAAAAAA   AAAA      AAAAAA  AAAA                AAAA   AAAA        AAAA AAAAAAAAAAAAAAAAA
   AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA      AAAAAAAA AAAA      AAAAAAAAA     AAAA       AAAAA  AAAA                AAAA   AAAA        AAAA AAAAAAAAAAAAAAAAA
  AAAAAAA  AAAAAAAAA  AAAAAAAAA AAAAAAAAA
`

export function ClientLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const [overlayHidden, setOverlayHidden] = useState(false)

  // Host apps self-wire the drawer's @-providers + empty-state suggestions via
  // window globals (see stream's lib/stream-chat). Read them reactively so they
  // appear without a per-app prop. Apps that set nothing → undefined → unchanged.
  const [hostChat, setHostChat] = useState<{
    providers?: Array<{ name: string; description: string; takesArg?: boolean }>
    suggestions?: string[]
  }>({})
  useEffect(() => {
    if (typeof window === "undefined") return
    const read = () => {
      const w = window as unknown as { __askfinzChatContextProviders?: unknown; __askfinzChatSuggestions?: unknown }
      setHostChat({
        providers: Array.isArray(w.__askfinzChatContextProviders) ? (w.__askfinzChatContextProviders as Array<{ name: string; description: string; takesArg?: boolean }>) : undefined,
        suggestions: Array.isArray(w.__askfinzChatSuggestions) ? (w.__askfinzChatSuggestions as string[]) : undefined,
      })
    }
    read()
    const t = setTimeout(read, 0) // catch globals set in a sibling mount effect
    window.addEventListener("askfinz-chat-config", read)
    return () => { clearTimeout(t); window.removeEventListener("askfinz-chat-config", read) }
  }, [pathname])

  useEffect(() => {
    const year = new Date().getFullYear()
    console.log(
      `%c${CONSOLE_ASCII_BANNER}`,
      'font-family: monospace; font-size: 5.5px; line-height: 1.1; color: #e53e3e;'
    )
    console.log(
      `%c© ${year} askFinz — a Paul V | Holdings entity. All rights reserved.`,
      'font-family: sans-serif; font-size: 13px; color: #718096; font-weight: 400; padding: 2px 0;'
    )
    console.log(
      `%cFor our policy on Dev-Tools refer to: https://askfinz.ai/dev-tools`,
      'font-family: sans-serif; font-size: 11px; color: #4A5568; padding: 1px 0;'
    )
    console.log(
      `%cAll legal information is available at https://askfinz.ai/legal/`,
      'font-family: sans-serif; font-size: 11px; color: #4A5568; padding: 1px 0;'
    )
    console.log(
      `%cLooking to join us? Visit https://askfinz.com/career for current job postings.`,
      'font-family: sans-serif; font-size: 11px; color: #4A5568; padding: 1px 0;'
    )
  }, [])

  // Compute the gradient mask for the bright banner layer — sweeps left-to-right
  const bannerMask = useMemo(() => {
    // progress goes 0→100; we want the bright region to sweep across
    const pct = Math.min(progress, 100);
    const edge = pct + 5; // small soft edge
    return `linear-gradient(90deg, #000 0%, #000 ${pct}%, transparent ${Math.min(edge, 100)}%, transparent 100%)`;
  }, [progress]);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false)
  const latestRelease = getLatestReleaseNote()

  // Immersive/lockdown surfaces (e.g. the Learn secure exam) can hide the global
  // ChatDrawer by dispatching `afn-chat:hide` (and `afn-chat:show` to restore).
  // Default visible; backward-compatible for every app that never fires it.
  const [hideChat, setHideChat] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const hide = () => setHideChat(true)
    const show = () => setHideChat(false)
    window.addEventListener("afn-chat:hide", hide)
    window.addEventListener("afn-chat:show", show)
    return () => { window.removeEventListener("afn-chat:hide", hide); window.removeEventListener("afn-chat:show", show) }
  }, [])

  const isProduction = process.env.NODE_ENV === "production"
  const gtmId = isProduction ? process.env.NEXT_PUBLIC_GTM_ID : null
  const gaId = isProduction ? (process.env.NEXT_PUBLIC_GA_ID ?? "G-849RCQSXKT") : null

  // Check if we're on an auth page
  const isAuthPage = pathname?.startsWith("/auth")

  const lastPathnameRef = useRef<string | null>(null)

  useEffect(() => {
    if (lastPathnameRef.current === pathname) {
      return
    }

    lastPathnameRef.current = pathname
    setProgress(0)
    setIsVisible(true)
    const duration = 2000
    const interval = 16
    const steps = duration / interval
    let currentStep = 0

    const timer = setInterval(() => {
      currentStep++
      const newProgress = (currentStep / steps) * 100
      setProgress(newProgress)
      if (currentStep >= steps) {
        clearInterval(timer)
        setProgress(100)
        setTimeout(() => setIsVisible(false), 300)
        setTimeout(() => setOverlayHidden(true), 600)
      }
    }, interval)

    const handleLoad = () => {
      clearInterval(timer)
      setProgress(100)
      setTimeout(() => setIsVisible(false), 300)
      setTimeout(() => setOverlayHidden(true), 600)
    }

    if (typeof window !== "undefined") {
      window.addEventListener("load", handleLoad)
    }

    return () => {
      clearInterval(timer)
      if (typeof window !== "undefined") {
        window.removeEventListener("load", handleLoad)
      }
    }
  }, [pathname])

  // Failsafe: the boot splash MUST always clear, even if the pathname-driven
  // hide above is short-circuited — e.g. on apps that mount under an optional
  // catch-all route ([[...slug]]) where usePathname() can resolve to the same
  // value the ref was initialised with, so the early-return guard skips the
  // timer and the overlay would stick forever. This mount-only timer guarantees
  // the overlay clears regardless.
  useEffect(() => {
    const t1 = setTimeout(() => setIsVisible(false), 1600)
    const t2 = setTimeout(() => setOverlayHidden(true), 1900)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  const releaseNotesCheckedRef = useRef<string | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return

    const isAuthPage = pathname?.startsWith("/auth")
    if (isAuthPage) return

    const checkKey = `${pathname}_${latestRelease.version}`
    if (releaseNotesCheckedRef.current === checkKey) {
      return
    }
    releaseNotesCheckedRef.current = checkKey

    // Check both localStorage (same-origin) and cookies (cross-subdomain)
    const cookieKey = `AskFinz_rn_${latestRelease.version}`
    const hasSeenLocal = localStorage.getItem(`AskFinz_release_notes_${latestRelease.version}`)
    const hasSeenCookie = document.cookie.split("; ").some(c => c.startsWith(`${cookieKey}=`))

    if (!hasSeenLocal && !hasSeenCookie) {
      const timer = setTimeout(() => {
        // Don't stack "What's New" on top of the first-run onboarding
        // ("Preparing Your Workspace"). A brand-new user has no prior version to
        // be updated about — they see onboarding first; release notes surface on
        // a later visit once onboarding is finished. Re-checked HERE (not at
        // effect time) because the onboarding key is written asynchronously, so
        // it may not exist yet when this effect first runs.
        const onboardingState = localStorage.getItem("askfinz_account_onboarding_state")
        if (onboardingState !== null && onboardingState !== "completed") {
          releaseNotesCheckedRef.current = null   // allow a re-check on next nav
          return
        }
        setShowReleaseNotes(true)
        // Persist in localStorage (same-origin fast check)
        localStorage.setItem(`AskFinz_release_notes_${latestRelease.version}`, "true")
        // Persist in cookie with domain=.askfinz.ai so ALL subdomains share it
        const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString() // 90 days
        const isAskFinz = window.location.hostname.endsWith("askfinz.ai")
        const domainAttr = isAskFinz ? "; domain=.askfinz.ai" : ""
        document.cookie = `${cookieKey}=1; path=/${domainAttr}; expires=${expires}; SameSite=Lax`
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [latestRelease.version, pathname])

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <Suspense fallback={null}>
        <AuthTokenHandler />
      </Suspense>
      <DevToolsDetector />
      <AnalyticsRecorder />
      <SidebarProvider>
        {/* SECURITY V-075: GTM and GA used to be inline scripts injected via
            `dangerouslySetInnerHTML`. That forces every CSP across the 22
            apps to keep `'unsafe-inline'` in `script-src`, which defeats the
            purpose of CSP. We now load them via `next/script` with `src=` to
            external `googletagmanager.com`, and bootstrap `dataLayer` on the
            global before that script runs.

            Foundational dependency: V-062. Once V-075 has shipped to all 22
            apps, the V-062 CSP module can drop `'unsafe-inline'` from
            `script-src` and only `script-src-elem` `https://www.googletagmanager.com`
            needs to be allowed. */}
        {gtmId && (
          <>
            {/* Initialise dataLayer on the window before the GTM loader runs.
                The empty array is enough — GTM is an SDK, not user code, so
                this is not "executable user content" by the V-062 rubric. */}
            <Script
              id="gtm-init"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`,
              }}
            />
            <Script
              id="gtm-loader"
              strategy="afterInteractive"
              src={`https://www.googletagmanager.com/gtm.js?id=${gtmId}`}
            />
          </>
        )}
        {/* Google Analytics — bot-guarded. Previously the loader + config
            fired for ALL traffic, so our crawler fleet / edge nodes / uptime
            monitors (headless browsers) polluted GA4 (e.g. the askfinz.ai
            property's "Unassigned" bucket = ~1.6k sessions at 0 engagement).
            We now dynamically inject the loader + call config ONLY for real
            users — navigator.webdriver=false and a non-bot UA. The loader is
            no longer emitted for automated traffic at all. */}
        {gaId && (
          <Script
            id="ga-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(){try{
  var n=navigator,ua=(n&&n.userAgent)||'';
  if((n&&n.webdriver===true)||/bot|crawl|spider|slurp|headless|puppeteer|playwright|lighthouse|pagespeed|gtmetrix|pingdom|uptime|monitor|axios|node-fetch|python-requests|curl|wget|askfinz/i.test(ua)){return;}
  var g=document.createElement('script');g.async=1;g.src='https://www.googletagmanager.com/gtag/js?id=${gaId}';document.head.appendChild(g);
  window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${gaId}');
}catch(e){}})();`,
            }}
          />
        )}
        {/* Full-screen AsciiBanner loading overlay */}
        {!overlayHidden && (
          <div className={`askfinz-loading-overlay ${!isVisible ? 'hidden' : ''}`}>
            <div className="askfinz-loading-banner-wrap">
              {/* Dim base layer */}
              <div className="askfinz-loading-banner-dim">
                <AsciiBanner className="askfinz-banner-transparent" />
              </div>
              {/* Bright reveal layer — gradient mask driven by progress */}
              <div
                className="askfinz-loading-banner-bright"
                style={{
                  maskImage: bannerMask,
                  WebkitMaskImage: bannerMask,
                }}
              >
                <AsciiBanner className="askfinz-banner-transparent" />
              </div>
            </div>
            <div className="askfinz-loading-indicator">
              <div className="askfinz-loading-spinner" />
              <span className="askfinz-loading-text">Loading</span>
            </div>
          </div>
        )}
        {/* Thin progress bar (secondary) */}
        <div
          className="page-loading-bar"
          style={{
            opacity: isVisible ? 1 : 0,
            visibility: isVisible ? "visible" : "hidden"
          }}
        >
          <div className="page-loading-bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <DefaultAuthGuard>
          <div className="min-h-screen w-full">
            {/* Google Tag Manager (Noscript) */}
            {gtmId && (
              <noscript>
                <iframe
                  src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
                  height="0"
                  width="0"
                  style={{ display: "none", visibility: "hidden" }}
                />
              </noscript>
            )}
            {children}
            {!isAuthPage && <Footer onVersionClick={() => setShowReleaseNotes(true)} />}
            {!isAuthPage && !hideChat && <ChatDrawer apiBaseUrl={process.env.NEXT_PUBLIC_CHAT_APP_URL ?? "https://chat.askfinz.ai"} getAuthToken={getAuthToken} sourceApp={typeof window !== "undefined" ? window.location.hostname : "askfinz"} metadata={() => {
              if (typeof window === "undefined") return {}
              const g = (window as unknown as { __askfinzChatMetadata?: () => Record<string, unknown> }).__askfinzChatMetadata
              try { return typeof g === "function" ? (g() || {}) : {} } catch { return {} }
            }} resolveContextBlock={resolveCombinedContextBlock} getSurfaceHint={webIdeSurfaceHint} onSurfaceHintClick={(msg) => { void webIdeHandover(msg) }} handoverInbox={webIdeHandoverInbox} contextProviders={(/\/project\//.test(pathname ?? "") ? WEBIDE_CONTEXT_PROVIDERS : undefined) ?? hostChat.providers} suggestions={hostChat.suggestions} />}
          </div>
        </DefaultAuthGuard>
        <ReleaseNotesDialog
          open={showReleaseNotes}
          onOpenChange={setShowReleaseNotes}
          releaseNote={latestRelease}
        />
        <Toaster
          position="top-right"
          offset={{ top: 60 }}
        />
      </SidebarProvider>
    </ThemeProvider>
  )
}

